import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.header 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black/90 backdrop-blur-xl border-b border-white/10 shadow-2xl' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-3">
            <motion.div 
              className="flex items-center gap-3"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <div className="relative">
                <img 
                  src="/supaaid.jpeg" 
                  alt="SupaAid Logo" 
                  className="w-12 h-12 object-contain rounded-lg"
                />
                <motion.div
                  className="absolute inset-0"
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.3, 0.7, 0.3]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <div className="w-12 h-12 bg-blue-400/30 rounded-lg" />
                </motion.div>
              </div>
              <span className="text-2xl font-bold text-white">
                SupaAid
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              { href: '#emergency', label: 'Emergency Coordination' },
              { href: '#ai-doctor', label: 'AI Doctor' },
              { href: '/about', label: 'About', isRoute: true },
              { href: '#contact', label: 'Contact' }
            ].map((link, index) => (
              link.isRoute ? (
                <Link
                  key={index}
                  to={link.href}
                  className="text-white/80 hover:text-white transition-colors font-medium relative group"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300" />
                </Link>
              ) : (
                <motion.a
                  key={index}
                  href={link.href}
                  className="text-white/80 hover:text-white transition-colors font-medium relative group"
                  whileHover={{ scale: 1.05 }}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300" />
                </motion.a>
              )
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden pb-6 space-y-2 mt-4 bg-blue-800/50 backdrop-blur-lg rounded-2xl p-4"
          >
            {[
              { href: '#emergency', label: 'Emergency Coordination' },
              { href: '#ai-doctor', label: 'AI Doctor' },
              { href: '/about', label: 'About', isRoute: true },
              { href: '#contact', label: 'Contact' }
            ].map((link, index) => (
              link.isRoute ? (
                <Link
                  key={index}
                  to={link.href}
                  className="block text-white/90 hover:text-white hover:bg-white/10 transition-all duration-200 font-medium py-3 px-4 rounded-xl"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ) : (
                <motion.a
                  key={index}
                  href={link.href}
                  className="block text-white/90 hover:text-white hover:bg-white/10 transition-all duration-200 font-medium py-3 px-4 rounded-xl"
                  whileHover={{ x: 5, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </motion.a>
              )
            ))}
          </motion.nav>
        )}
      </div>
    </motion.header>
  );
};

export default Header;
